<script setup>
import { computed, nextTick, ref } from 'vue';
import { useI18n } from 'vue-i18n';
import Button from 'dashboard/components-next/button/Button.vue';
import InternalChatEditor from './InternalChatEditor.vue';
import {
  legacyMentionAttributes,
  reconciledMentionAttributes,
} from './internalChatMentions';

const props = defineProps({
  message: { type: Object, required: true },
  saveMessage: { type: Function, required: true },
  members: { type: Object, default: () => ({ users: [], teams: [] }) },
  canMentionAll: { type: Boolean, default: false },
});
const emit = defineEmits(['close']);
const { t } = useI18n();
const editor = ref(null);
const content = ref(props.message.content || '');
const legacyMentions = legacyMentionAttributes(props.message);
const contentFormat = ref(
  props.message.content_attributes?.content_format || 'plain'
);
const contentEmpty = ref(!content.value.trim());
const isSaving = ref(false);
const error = ref('');
const length = computed(() => Array.from(content.value).length);
const validationError = computed(() =>
  length.value > 10_000
    ? t('INTERNAL_CHAT.CONTENT_TOO_LONG', {
        count: length.value,
        limit: 10_000,
      })
    : ''
);
const canSave = computed(
  () =>
    !validationError.value &&
    Boolean(!contentEmpty.value || props.message.attachments?.length)
);
const save = async () => {
  if (isSaving.value || !canSave.value) return;
  isSaving.value = true;
  error.value = '';
  try {
    await props.saveMessage({
      message: props.message,
      content: content.value,
      contentFormat: contentFormat.value,
      ...reconciledMentionAttributes({
        content: content.value,
        contentFormat: contentFormat.value,
        members: props.members,
        previous: legacyMentions,
      }),
    });
    emit('close');
  } catch {
    error.value = t('INTERNAL_CHAT.ACTION_FAILED');
  } finally {
    isSaving.value = false;
  }
  if (error.value) {
    await nextTick();
    editor.value?.focus();
  }
};
</script>

<template>
  <div class="min-w-0 text-start">
    <InternalChatEditor
      ref="editor"
      v-model="content"
      v-model:content-format="contentFormat"
      :label="t('INTERNAL_CHAT.EDIT_MESSAGE')"
      :disabled="isSaving"
      :can-submit="canSave"
      :members="members"
      :can-mention-all="canMentionAll"
      :editor-key="String(message.id)"
      test-id="internal-chat-edit-input"
      focus-on-mount
      @empty-change="contentEmpty = $event"
      @submit="save"
    />
    <p
      v-if="error || validationError"
      class="mb-0 mt-2 text-xs text-n-ruby-11"
      role="alert"
    >
      {{ error || validationError }}
    </p>
    <div class="mt-2 flex justify-end gap-2">
      <Button
        type="button"
        variant="faded"
        color="slate"
        size="xs"
        :label="t('INTERNAL_CHAT.CANCEL')"
        :disabled="isSaving"
        @click="emit('close')"
      />
      <Button
        type="button"
        size="xs"
        :label="t('INTERNAL_CHAT.SAVE')"
        :aria-label="t('INTERNAL_CHAT.SAVE')"
        :is-loading="isSaving"
        :disabled="isSaving || !canSave"
        @click="save"
      />
    </div>
  </div>
</template>
